import React from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/UI/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { Experience, PersonalInfo } from '../types';

interface ExperienceTimelineProps {
  experiences: Experience[];
  resumeUrl?: string;
  contactEmail?: string;
  personalInfo: PersonalInfo;
}

const ExperienceTimeline = ({ experiences, resumeUrl, contactEmail, personalInfo }: ExperienceTimelineProps) => {
  return (
    <section className="py-24 relative bg-[var(--color-background)]" id="experience">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        {/* Section Header */}
        <div className="max-w-2xl mb-20 text-center mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full bg-[#0C2C39]/5 text-[#0C2C39]/60 text-[10px] font-black uppercase tracking-[0.3em] mb-6"
          >
            {personalInfo.experienceBadge || "Career Roadmap"}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-roboto text-4xl sm:text-5xl font-black text-[#0C2C39] mb-6 tracking-tight leading-[1.1]"
          >
            {personalInfo.experienceTitle || "Engineering Milestones"}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-[#0C2C39]/60 font-medium"
          >
            {personalInfo.experienceSubtitle || "A technical narrative of building scalable solutions and leading digital transformations."}
          </motion.p>
        </div>

        {/* Timeline Container */}
        <div className="relative max-w-5xl mx-auto">
          {/* Vertical Line - Teal Accent */}
          <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-1 bg-[#0C2C39]/10 -translate-x-1/2 hidden md:block rounded-full" />

          {/* Items */}
          <div className="space-y-16 relative">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`flex flex-col md:flex-row items-center justify-between w-full ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
              >
                {/* Content Card - White Neubrutal Card */}
                <div className={`w-full md:w-[46%] ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <div className="neubrutal-card p-8 bg-white group">
                    <div className={`flex flex-col mb-6 ${index % 2 === 0 ? 'md:items-end' : 'md:items-start'}`}>
                      <div className="w-14 h-14 rounded-2xl overflow-hidden mb-5 border-2 border-[#0C2C39] p-3 bg-[#FFF9EB] group-hover:bg-[#0C2C39] group-hover:text-white transition-all duration-300">
                        <Image src={exp.logo} alt={exp.logoAlt} className="w-full h-full object-contain grayscale-0 brightness-110" />
                      </div>
                      <h3 className="font-roboto text-2xl font-black text-[#0C2C39] tracking-tight">{exp.position}</h3>
                      <p className="text-sm font-black text-teal-700 uppercase tracking-widest mt-1">{exp.company}</p>
                    </div>

                    <div className={`flex items-center gap-2 mb-6 text-[10px] font-black uppercase tracking-widest text-[#0C2C39]/40 ${index % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                      <Icon name="Calendar" size={12} />
                      <span className="bg-[#0C2C39]/5 px-2 py-0.5 rounded-sm">{exp.duration}</span>
                    </div>

                    <p className="text-[#0C2C39]/70 text-sm leading-relaxed mb-6 font-medium">
                      {exp.description}
                    </p>

                    <div className={`flex flex-wrap gap-2 ${index % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                      {exp.technologies.map((tech, i) => (
                        <span key={i} className="px-3 py-1 bg-white text-[#0C2C39] text-[9px] font-black uppercase tracking-wider rounded-sm border-2 border-[#0C2C39]">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Center Dot - High Contrast */}
                <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white border-4 border-[#0C2C39] z-10 items-center justify-center shadow-md">
                  <div className="w-2.5 h-2.5 bg-[#0C2C39] rounded-full animate-pulse" />
                </div>

                {/* Empty Space for layout */}
                <div className="hidden md:block w-[46%]" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Executive CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 max-w-5xl mx-auto rounded-[2rem] p-12 text-center bg-white border-2 border-[#0C2C39] shadow-[6px_6px_0px_0px_#0C2C39] overflow-hidden relative group"
        >
          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-1.5 rounded-full bg-[#0C2C39]/5 text-teal-700 text-[9px] font-black uppercase tracking-[0.4em] mb-8"
            >
              Engagement Strategy
            </motion.div>
            <h3 className="font-roboto text-3xl sm:text-5xl font-black text-[#0C2C39] mb-8 tracking-tighter leading-[1.1] max-w-2xl mx-auto">
              Ready to architect <br />
              <span className="text-teal-700/80 italic">the next digital era?</span>
            </h3>
            <p className="text-[#0C2C39]/60 mb-12 max-w-lg mx-auto font-medium text-lg leading-relaxed">
              Currently accepting strategic partnerships and high-impact engineering roles.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 font-roboto">
              <a href={`mailto:${contactEmail || 'yabetskifle9@gmail.com'}`} className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto px-10 py-5 bg-[#0C2C39] text-white font-black uppercase tracking-widest text-[10px] rounded-[11px] shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] hover:shadow-none transition-all duration-300">
                  Initialize Engagement
                </Button>
              </a>
              <a href={resumeUrl || "/resume.pdf"} download className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto px-10 py-5 bg-white border-2 border-[#0C2C39] text-[#0C2C39] font-black uppercase tracking-widest text-[10px] rounded-[11px] shadow-[4px_4px_0px_0px_#0C2C39] hover:shadow-none transition-all duration-300">
                  Review Executive CV
                </Button>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ExperienceTimeline;